// src/components/SimpleFormComponents/SimpleFormProviderInitializer.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import { useEffect, useMemo, useRef } from 'react';
import { useModuleStore } from 'stores/module';
import useZustand from 'hooks/useZustand';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleFormProviderInitializer = ({ data, isInitializedRef, initFn }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { setFoundCpf, setFoundZip, formatAddress, setDocument, form } = useSimpleFormContext();
  const storedCurrentPage = useZustand('module', 'currentPage');
  const currentPageData = storedCurrentPage?.data;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  // Priority: data prop > currentPage.data from store
  const effectiveData = useMemo(() => {
    return data || currentPageData;
  }, [data, currentPageData]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // Track which _id we've populated to handle navigation between documents
  const lastPopulatedIdRef = useRef(null);
  // Track data object reference — new ref after a GET refresh means form must be repopulated
  const lastPopulatedDataRef = useRef(null);

  // Extract _id to a variable for clearer dependency tracking
  const effectiveDataId = effectiveData?._id;

  useEffect(() => {
    // Skip if no data ID available
    if (!effectiveDataId) return;

    // Skip if form is not available yet
    if (!form) return;

    // Skip if we've already populated this exact document fetch.
    // Guard on both _id (navigation between docs) AND data object reference (server refresh
    // for the same doc returns a new object — that must repopulate the form so unsaved user
    // input is discarded).
    if (
      lastPopulatedIdRef.current === effectiveDataId &&
      lastPopulatedDataRef.current === effectiveData
    )
      return;

    // Also check the external isInitializedRef if provided
    if (
      isInitializedRef?.current &&
      lastPopulatedIdRef.current === effectiveDataId &&
      lastPopulatedDataRef.current === effectiveData
    )
      return;

    // Update internal state if functions are available
    if (setFoundCpf) {
      setFoundCpf(effectiveData);
    }

    if (setFoundZip && formatAddress) {
      setFoundZip(formatAddress(effectiveData));
    }

    // resetFields() before setFieldsValue: clears nested paths (e.g. fiscalData.icms.cst) that setFieldsValue cannot clear by setting their parent to null.
    form.resetFields();

    const dataSet = {
      ...effectiveData,
      ...(formatAddress ? formatAddress(effectiveData) : {}),
    };

    form.setFieldsValue(dataSet);

    // Track that we've populated this document fetch
    lastPopulatedIdRef.current = effectiveDataId;
    lastPopulatedDataRef.current = effectiveData;

    // Mark external ref as initialized if provided
    if (isInitializedRef) {
      isInitializedRef.current = true;
    }

    // Call initialization function if provided
    if (initFn) {
      (async () => await initFn())();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveDataId, effectiveData, form]);

  // REMOVED: setFunctions({ form }) — form is already exposed by SimpleFormProvider
  // via registerProviderContext('form', exposedValues).
  // Access via: const { form } = useModuleContext() or useSimpleFormContext()

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return null;
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleFormProviderInitializer;

// *******************************************************************************
// HELPERS
// *******************************************************************************
